import React from 'react';
import PropTypes from 'prop-types';
import '../form_layout/form_layout.css';

import { IstTimePicker } from '../care_request/ist_time_picker';
import {
  REQUESTED_AT_IN_FUTURE,
  cancel_bucket_text,
  class_start_ist_text,
  is_requested_at_in_future,
  request_time_fact
} from './bucket_rules';

// The request time is the only input; under it, a plain read-only time fact (no category or pay wording).
// Without onRequestedAtChange the host owns the time field (Care draft "Requested at (IST)") and
// this shows only the time fact.
function TeacherCancelBucketPicker(props) {
  const {
    requestedAt,
    onRequestedAtChange,
    sessionStartEpoch,
    ianaTimezone,
    timezoneLabel,
    error,
    timeFieldNote,
    factNote,
    required,
    bookedAt
  } = props;
  const ownsTimeField = typeof onRequestedAtChange === 'function';
  const now_ms = Date.now();
  // a plain time fact, no category or pay wording (owner rule); '' for an empty, invalid or future time
  const fact = request_time_fact(sessionStartEpoch, requestedAt, ianaTimezone, now_ms);
  // capture mode: the Care draft field reports its own future-time error
  const inFuture = ownsTimeField && is_requested_at_in_future(requestedAt, ianaTimezone, now_ms);

  const heading_id = 'tp-cancel-bucket-heading';
  const category_id = 'tp-cancel-bucket-category';
  const start_id = 'tp-cancel-bucket-start';
  const error_id = 'tp-cancel-bucket-error';
  const zone = timezoneLabel || ianaTimezone;
  const start_text = ownsTimeField
    ? class_start_ist_text(sessionStartEpoch, ianaTimezone, zone)
    : '';
  const show_error = inFuture || error;
  const described_by = [
    start_text ? start_id : '',
    category_id,
    required ? '' : 'tp-cancel-bucket-keep',
    show_error ? error_id : ''
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      role={ownsTimeField ? undefined : 'group'}
      aria-labelledby={ownsTimeField ? undefined : heading_id}
      className="myt-field"
    >
      {ownsTimeField ? null : (
        <div id={heading_id} className="myt-form-label">
          When did the teacher ask to cancel? (IST)
        </div>
      )}
      {ownsTimeField ? (
        <div>
          <label className="myt-form-label" htmlFor="tp-cancel-bucket-time">
            {`When did the teacher ask to cancel? (${zone}${required ? ', required' : ''})`}
          </label>
          <IstTimePicker
            id="tp-cancel-bucket-time"
            value={requestedAt || ''}
            onChange={onRequestedAtChange}
            ianaTimezone={ianaTimezone}
            zoneLabel={zone}
            ariaRequired={required ? 'true' : 'false'}
            ariaInvalid={error || inFuture ? 'true' : 'false'}
            ariaDescribedBy={described_by}
            className="tp-cancel-bucket-time"
            bookedAt={bookedAt}
          />
          {start_text ? (
            <p id={start_id} className="myt-form-help" data-testid="tp-cancel-bucket-start">
              {start_text}
            </p>
          ) : null}
          {required ? null : (
            <p id="tp-cancel-bucket-keep" className="myt-form-help">
              Leave blank to keep the saved time.
            </p>
          )}
        </div>
      ) : null}
      <div
        id={category_id}
        role="status"
        data-testid="tp-cancel-bucket-category"
        className="myt-form-help"
      >
        {cancel_bucket_text({ fact, ownsTimeField, timeFieldNote, factNote })}
      </div>
      {inFuture ? (
        <p id={error_id} role="alert" className="myt-form-error">
          * {REQUESTED_AT_IN_FUTURE}.
        </p>
      ) : null}
      {error && !inFuture ? (
        <p id={error_id} role="alert" className="myt-form-error">
          * Enter when the teacher requested this cancellation ({zone}). Use your best estimate if
          unsure.
        </p>
      ) : null}
    </div>
  );
}

TeacherCancelBucketPicker.propTypes = {
  requestedAt: PropTypes.string,
  onRequestedAtChange: PropTypes.func,
  sessionStartEpoch: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ianaTimezone: PropTypes.string,
  timezoneLabel: PropTypes.string,
  error: PropTypes.bool,
  timeFieldNote: PropTypes.string,
  factNote: PropTypes.string,
  required: PropTypes.bool,
  bookedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

TeacherCancelBucketPicker.defaultProps = {
  requestedAt: '',
  onRequestedAtChange: undefined,
  sessionStartEpoch: undefined,
  ianaTimezone: 'Asia/Kolkata',
  timezoneLabel: 'IST',
  error: false,
  timeFieldNote: 'enter "Requested at (IST)" below',
  factNote: '',
  required: true,
  bookedAt: undefined
};

export default TeacherCancelBucketPicker;
